import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase, SUPABASE_FUNCTIONS_URL, SUPABASE_ANON_KEY } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";

export type CommentMatchLevel = "exact" | "project_only" | "override" | "skipped";

export interface CommentPreviewItem {
  monday_item_id: number;
  name: string;
  group_title: string | null;
  project_code: string | null;
  project_id: string | null;
  match_level: CommentMatchLevel;
  skip_reason?: string;
  updates_count: number;
  replies_count: number;
  attachments_count: number;
  likes_count: number;
}

export interface CommentImportOverride {
  project_id?: string | null;
  skip?: boolean;
}

export interface CommentImportStats {
  totalItems: number;
  matched: number;
  skipped: number;
  commentsUpserted: number;
  repliesUpserted: number;
  attachmentsUpserted: number;
  likesUpserted: number;
  skippedNoAuthor: number;
  matchDistribution: Record<CommentMatchLevel, number>;
}

export interface CommentImportResponse {
  success: boolean;
  stats?: CommentImportStats;
  preview?: CommentPreviewItem[];
  errors?: string[];
  error?: string;
}

interface CommentImportInput {
  boardId: number;
  groupIds: string[];
  dryRun: boolean;
  triggeredBy?: string | null;
  overrides?: Record<string, CommentImportOverride>;
  projectCodes?: string[];
}

async function callFunction(input: CommentImportInput): Promise<CommentImportResponse> {
  // MEDIUM-11: import-monday-comments krever nå admin — send session-token.
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/import-monday-comments`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session?.access_token ?? ""}`,
      apikey: SUPABASE_ANON_KEY,
    },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`import-monday-comments (${res.status}): ${text.slice(0, 300)}`);
  }
  return (await res.json()) as CommentImportResponse;
}

/** Trigger import (eller dry-run) av Monday-prosjektoppdateringer. */
export function useMondayCommentsImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CommentImportInput): Promise<CommentImportResponse> => {
      const res = await callFunction(input);
      if (!res.success) throw new Error(res.error ?? "Import feilet");
      return res;
    },
    onSuccess: async (data, vars) => {
      await invalidateFor(qc, "import_log");
      if (!vars.dryRun) {
        // Bred invalidering: én comments-import oppdaterer prosjekt-
        // kommentarer på tvers av mange prosjekter (board-gruppene
        // representerer flere prosjekter), så vi kan ikke målrette én
        // projectId her (VIB-649). invalidateFor uten projectId faller
        // tilbake til prefix-invalidering av alle relevante keys.
        await invalidateFor(qc, "project_comments");
        const s = data.stats;
        toast.success(
          s
            ? `Importert ${s.commentsUpserted} kommentarer + ${s.repliesUpserted} svar fra ${s.matched} prosjekter`
            : "Import fullført",
        );
      }
    },
    onError: (err) => {
      toast.error(`Import feilet: ${(err as Error).message}`);
    },
  });
}
